import { Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { setAvailableOrgList } from '../../../redux/slices/entityInstanceSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import {
    exportTemplateForBulkUpload,
    importBulkUploadFromExcel,
} from '../../../services/uploadManagerService'
import {
    BulkUploadDialog,
    BulkUploadDownloadActions,
    BulkUploadDropzone,
    BulkUploadSelect,
    BulkUploadUploadActions,
} from './BulkUploadUI'
import { BulkUploadModalProps, Template } from './interface'
import ProgramSelector from './ProgramSelector'
import { useBulkUploadDropzone } from './useBulkUploadDropzone'
import {
    filterDeviceTemplates,
    getAutoSelectedTemplateId,
    isProgramSelectionMandatory,
} from './utils'

interface OrgWithTemplates {
    id: string
    templates?: Template[]
}

const UploadManagerBulkUploadModal = ({
    open,
    onClose,
    templates: propTemplates = [],
    initialTemplateId = '',
    mode = 'both',
    showSubTypes = true,
    onUploadSuccess,
}: BulkUploadModalProps): React.ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const availableOrgList = useAppSelector(
        (state) => state.entityInstance.availableOrgList || [],
    )
    const loggedInOrgId = useAppSelector((state) => state.org.org?.id)
    const [uploadSuccess, setUploadSuccess] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [selectedProgramIdForUpload, setSelectedProgramIdForUpload] =
        useState('')
    const [selectedProgramIdForDownload, setSelectedProgramIdForDownload] =
        useState('')
    const [selectedTemplateForUpload, setSelectedTemplateForUpload] =
        useState(initialTemplateId)
    const [selectedSubTemplateForUpload, setSelectedSubTemplateForUpload] =
        useState('')
    const [selectedTemplateForSave, setSelectedTemplateForSave] =
        useState(initialTemplateId)
    const [selectedSubTemplateForSave, setSelectedSubTemplateForSave] =
        useState('')

    const isInitialized = useRef(false)

    useEffect(() => {
        if (!open) {
            isInitialized.current = false
            return
        }

        if (
            open &&
            !isInitialized.current &&
            (initialTemplateId || (propTemplates && propTemplates.length > 0))
        ) {
            setSelectedFile(null)
            setSelectedProgramIdForUpload('')
            setSelectedProgramIdForDownload('')

            let uploadAutoSelected = ''
            let downloadAutoSelected = ''

            if (!initialTemplateId && propTemplates) {
                uploadAutoSelected = getAutoSelectedTemplateId(propTemplates)
                downloadAutoSelected = getAutoSelectedTemplateId(propTemplates)
            }

            setSelectedTemplateForUpload(
                initialTemplateId || uploadAutoSelected,
            )
            setSelectedSubTemplateForUpload('')

            setSelectedTemplateForSave(
                initialTemplateId || downloadAutoSelected,
            )
            setSelectedSubTemplateForSave('')

            const initialTemplateForSubtype = propTemplates?.find(
                (t) => t.id === (initialTemplateId || uploadAutoSelected),
            )
            if (
                initialTemplateForSubtype?.subEntityTypes &&
                initialTemplateForSubtype.subEntityTypes.length > 0
            ) {
                setSelectedSubTemplateForUpload(
                    initialTemplateForSubtype.subEntityTypes[0].id,
                )
                setSelectedSubTemplateForSave(
                    initialTemplateForSubtype.subEntityTypes[0].id,
                )
            }
            isInitialized.current = true
        }
    }, [open, initialTemplateId, propTemplates])

    const subEntitiesForUpload = useMemo(() => {
        if (propTemplates.length > 0 && !selectedTemplateForUpload) return []

        const effectiveProgramId =
            selectedProgramIdForUpload ||
            (availableOrgList?.length === 1 ? availableOrgList[0].id : '')

        if (!effectiveProgramId) return []

        const selectedProgram = availableOrgList.find(
            (org: OrgWithTemplates) => org.id === effectiveProgramId,
        )

        if (!selectedProgram?.templates) return []

        // if program has only one template â†’ no subtypes
        if (selectedProgram.templates.length <= 1) return []

        return selectedProgram.templates
    }, [
        selectedProgramIdForUpload,
        availableOrgList,
        propTemplates.length,
        selectedTemplateForUpload,
    ])

    const subEntitiesForDownload = useMemo(() => {
        if (propTemplates.length > 0 && !selectedTemplateForSave) return []

        const effectiveProgramId =
            selectedProgramIdForDownload ||
            (availableOrgList?.length === 1 ? availableOrgList[0].id : '')

        if (!effectiveProgramId) return []

        const selectedProgram = availableOrgList.find(
            (org: OrgWithTemplates) => org.id === effectiveProgramId,
        )

        if (!selectedProgram?.templates) return []

        if (selectedProgram.templates.length <= 1) return []

        return selectedProgram.templates
    }, [
        selectedProgramIdForDownload,
        availableOrgList,
        propTemplates.length,
        selectedTemplateForSave,
    ])

    useEffect(() => {
        if (!open) return
        if (subEntitiesForUpload.length === 0) {
            setSelectedSubTemplateForUpload('')
        }
    }, [open, subEntitiesForUpload])

    useEffect(() => {
        if (!open) return
        if (subEntitiesForDownload.length === 0) {
            setSelectedSubTemplateForSave('')
        }
    }, [open, subEntitiesForDownload])

    const resetModalState = useCallback(() => {
        setSelectedFile(null)
        setSelectedProgramIdForUpload('')
        setSelectedProgramIdForDownload('')
        setSelectedTemplateForUpload('')
        setSelectedSubTemplateForUpload('')
        setSelectedTemplateForSave('')
        setSelectedSubTemplateForSave('')
    }, [])

    useEffect(() => {
        if (!open || !loggedInOrgId) return

        const templateId =
            selectedTemplateForUpload ||
            selectedTemplateForSave ||
            initialTemplateId

        if (templateId) {
            dispatch(setAvailableOrgList({ orgId: loggedInOrgId, templateId }))
        }
    }, [
        open,
        loggedInOrgId,
        selectedTemplateForUpload,
        selectedTemplateForSave,
        initialTemplateId,
        dispatch,
    ])

    const { getRootProps, getInputProps, isDragActive } =
        useBulkUploadDropzone(setSelectedFile)

    const handleUpload = useCallback(async () => {
        const finalTemplateId =
            selectedSubTemplateForUpload || selectedTemplateForUpload
        if (!selectedFile || !finalTemplateId) return

        const isProgram = !!selectedProgramIdForUpload

        dispatch(updateLoader(true))
        try {
            const result = await importBulkUploadFromExcel(
                selectedFile,
                loggedInOrgId || '',
                finalTemplateId,
                isProgram,
                selectedProgramIdForUpload,
            )

            if (result.type === 'success') {
                setUploadSuccess(true)
                setSelectedFile(null)
                setSelectedSubTemplateForUpload('')

                if (onUploadSuccess) {
                    onUploadSuccess()
                }

                setTimeout(() => {
                    setUploadSuccess(false)
                    resetModalState()
                    onClose()
                })
            }
        } finally {
            dispatch(updateLoader(false))
        }
    }, [
        selectedFile,
        selectedSubTemplateForUpload,
        selectedTemplateForUpload,
        loggedInOrgId,
        selectedProgramIdForUpload,
        dispatch,
        resetModalState,
        onClose,
        onUploadSuccess,
    ])

    const handleSave = useCallback(async () => {
        const finalTemplateId =
            selectedSubTemplateForSave || selectedTemplateForSave
        if (!finalTemplateId) return

        const isProgram = !!selectedProgramIdForDownload

        dispatch(updateLoader(true))
        try {
            await exportTemplateForBulkUpload(
                finalTemplateId,
                loggedInOrgId || '',
                isProgram,
                selectedProgramIdForDownload,
            )
            setSelectedSubTemplateForSave('')
            onClose()
        } finally {
            dispatch(updateLoader(false))
        }
    }, [
        selectedSubTemplateForSave,
        selectedTemplateForSave,
        loggedInOrgId,
        selectedProgramIdForDownload,
        dispatch,
        onClose,
    ])

    const isProgramSelectionMandatoryForUpload = useMemo(() => {
        return isProgramSelectionMandatory(
            availableOrgList,
            selectedProgramIdForUpload,
        )
    }, [availableOrgList, selectedProgramIdForUpload])

    const isProgramSelectionMandatoryForDownload = useMemo(() => {
        return isProgramSelectionMandatory(
            availableOrgList,
            selectedProgramIdForDownload,
        )
    }, [availableOrgList, selectedProgramIdForDownload])

    return (
        <BulkUploadDialog
            open={open}
            onClose={() => {
                resetModalState()
                onClose()
            }}
            title={t('uploadManager.bulkUpload.title')}
            closeLabel={t('close')}
        >
            {/* Upload Section */}
            {(mode === 'upload' || mode === 'both') && (
                <>
                    {/* 1. Template Selection for Upload */}
                    {propTemplates.length > 0 && (
                        <BulkUploadSelect
                            id="upload-template-select"
                            label={t(
                                'uploadManager.bulkUpload.selectTemplateForUpload',
                            )}
                            value={selectedTemplateForUpload}
                            onChange={(e) => {
                                const newTemplateId = e.target.value as string
                                setSelectedTemplateForUpload(newTemplateId)
                                // Reset program and sub-type when main template changes
                                setSelectedProgramIdForUpload('')
                                setSelectedSubTemplateForUpload('')
                            }}
                            options={filterDeviceTemplates(propTemplates)}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectTemplate',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    propTemplates.find((t) => t.id === selected)
                                        ?.name || ''
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectTemplate',
                                        )}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    {/* 2. Program Selection for Upload */}
                    {selectedTemplateForUpload &&
                        Array.isArray(availableOrgList) &&
                        availableOrgList.length > 1 && (
                            <ProgramSelector
                                selectedProgramId={selectedProgramIdForUpload}
                                onProgramChange={(newProgramId) => {
                                    setSelectedProgramIdForUpload(newProgramId)
                                }}
                                availableOrgList={availableOrgList}
                            />
                        )}

                    {/* 3. Sub Entity Type Selection for Upload */}
                    {showSubTypes && subEntitiesForUpload.length > 0 && (
                        <BulkUploadSelect
                            id="upload-subtype-select"
                            label={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            value={selectedSubTemplateForUpload}
                            onChange={(e) =>
                                setSelectedSubTemplateForUpload(e.target.value)
                            }
                            options={filterDeviceTemplates(
                                subEntitiesForUpload,
                            )}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    subEntitiesForUpload.find(
                                        (sub: Template) => sub.id === selected,
                                    )?.name || ''
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectSubEntityType',
                                        )}
                                    </Typography>
                                )
                            }
                            sx={{ mb: 3 }}
                        />
                    )}

                    {/* Dropzone */}
                    <BulkUploadDropzone
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        selectedFileName={selectedFile?.name}
                        selectFileLabel={t(
                            'uploadManager.bulkUpload.selectFileToUpload',
                        )}
                        dragAndDropLabel={t(
                            'uploadManager.bulkUpload.dragAndDrop',
                        )}
                    />

                    {/* Upload Button */}
                    <BulkUploadUploadActions
                        uploadSuccess={uploadSuccess}
                        successMessage={t(
                            'uploadManager.bulkUpload.fileUploadedSuccess',
                        )}
                        uploadFileLabel={t('uploadFile')}
                        onUpload={handleUpload}
                        disabled={
                            !selectedFile ||
                            !selectedTemplateForUpload ||
                            isProgramSelectionMandatoryForUpload
                        }
                    />
                </>
            )}

            {/* Download Template Section */}
            {(mode === 'download' || mode === 'both') && (
                <>
                    {/* 1. Template Selection for Download */}
                    {propTemplates.length > 0 && (
                        <BulkUploadSelect
                            id="download-template-select"
                            label={t(
                                'uploadManager.bulkUpload.selectDownloadTemplate',
                            )}
                            value={selectedTemplateForSave}
                            onChange={(e) => {
                                const newTemplateId = e.target.value as string
                                setSelectedTemplateForSave(newTemplateId)
                                setSelectedProgramIdForDownload('')
                                setSelectedSubTemplateForSave('')
                            }}
                            options={filterDeviceTemplates(propTemplates)}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectTemplate',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    propTemplates.find((t) => t.id === selected)
                                        ?.name || ''
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectTemplate',
                                        )}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    {/* 2. Program Selection for Download */}
                    {selectedTemplateForSave &&
                        Array.isArray(availableOrgList) &&
                        availableOrgList.length > 1 && (
                            <ProgramSelector
                                selectedProgramId={selectedProgramIdForDownload}
                                onProgramChange={(newProgramId) => {
                                    setSelectedProgramIdForDownload(
                                        newProgramId,
                                    )
                                }}
                                availableOrgList={availableOrgList}
                            />
                        )}

                    {/* 3. Sub Entity Type Selection for Download */}
                    {showSubTypes && subEntitiesForDownload.length > 0 && (
                        <BulkUploadSelect
                            id="download-subtype-select"
                            label={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            value={selectedSubTemplateForSave}
                            onChange={(e) =>
                                setSelectedSubTemplateForSave(e.target.value)
                            }
                            options={filterDeviceTemplates(
                                subEntitiesForDownload,
                            )}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    subEntitiesForDownload.find(
                                        (sub: Template) => sub.id === selected,
                                    )?.name || ''
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectSubEntityType',
                                        )}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, color: '#666' }}
                    >
                        {t('uploadManager.bulkUpload.downloadNote')}
                    </Typography>

                    <BulkUploadDownloadActions
                        onCancel={() => {
                            resetModalState()
                            onClose()
                        }}
                        onDownload={handleSave}
                        disabled={
                            (!selectedTemplateForSave &&
                                !selectedSubTemplateForSave) ||
                            isProgramSelectionMandatoryForDownload
                        }
                        cancelLabel={t('uploadManager.bulkUpload.cancel')}
                        downloadLabel={t(
                            'uploadManager.bulkUpload.downloadTemplate',
                        )}
                    />
                </>
            )}
        </BulkUploadDialog>
    )
}

export default UploadManagerBulkUploadModal
